// each change in this file should be reflected in vendict app - there's a copy of this file as well
import { brandingDefinitions } from './brandingDefinitions'

export const BrandingStructure = {
  mixins: [brandingDefinitions],
  computed: {
    brandingStructure() {
      // Helper function to generate visibility properties
      const generateVisibilityProperties = () => {
        const visibility = {
          header: undefined,
          subheader: true,
        }

        // Generate from aboutAndCertifications and pages
        const allModules = [
          ...this.brandingDefinitions.aboutAndCertifications,
          ...this.brandingDefinitions.pages,
        ]

        allModules.forEach((module) => {
          if (module.visibility !== false) {
            const headerKey =
              module.legacyNames?.visibilityHeader || `${module.prefix}Header`
            const captionKey = `${module.prefix}Caption`

            visibility[headerKey] = undefined
            visibility[captionKey] = undefined
          }
        })

        return visibility
      }

      // Helper function to generate overview properties
      const generateOverviewProperties = () => {
        const overview = {
          // Global properties
          footerHeaderColor: undefined,
          footerCaptionColor: undefined,
          headersFontColor: undefined,
          captionFontColor: undefined,
          headerSecurityColor: undefined,
          captionSecurityColor: undefined,
          headersFontSize: undefined,
          captionFontSize: undefined,
          header: undefined,
          subheader: undefined,
          backgroundColor: undefined,
        }

        // Generate from aboutAndCertifications
        this.brandingDefinitions.aboutAndCertifications.forEach((module) => {
          overview[`${module.prefix}HeaderFontSize`] = undefined
          overview[`${module.prefix}HeaderColor`] = undefined
          overview[`${module.prefix}CaptionFontSize`] = undefined
          overview[`${module.prefix}CaptionColor`] = undefined
          overview[`${module.prefix}Header`] = undefined
          overview[`${module.prefix}Subheader`] = undefined

          // Handle page subheader with legacy name mapping
          const pageSubheaderKey = module.legacyNames?.pageHeaderPrefix
            ? `${module.legacyNames.pageHeaderPrefix}PageSubheader`
            : `${module.prefix}PageSubheader`
          overview[pageSubheaderKey] = undefined
        })

        // Generate from pages
        this.brandingDefinitions.pages.forEach((module) => {
          // Font size properties (if not explicitly disabled)
          if (module.fontSize !== false) {
            overview[`${module.prefix}HeaderFontSize`] = undefined
            overview[`${module.prefix}CaptionFontSize`] = undefined
          }

          // Text content properties (if not explicitly disabled)
          if (module.textContent !== false) {
            // Handle special case for team: only pageSubheader
            const headerKey =
              module.legacyNames?.textContentHeader ||
              module.legacyNames?.visibilityHeader ||
              `${module.prefix}Header`
            const subheaderKey =
              module.legacyNames?.textContentSubheader ||
              `${module.prefix}Subheader`
            overview[headerKey] = undefined
            overview[subheaderKey] = undefined
            overview[`${module.prefix}PageSubheader`] = undefined
          }

          // Handle docs page subheader for securityLibrary
          if (module.legacyNames?.docsPageSubheader) {
            overview[module.legacyNames.docsPageSubheader] = undefined
          }
        })

        return overview
      }

      // Helper function to generate border color properties
      const generateBorderProperties = () => {
        const borders = {}

        // Generate from aboutAndCertifications
        this.brandingDefinitions.aboutAndCertifications.forEach((module) => {
          // borderColor defaults to true unless explicitly set to false
          if (module.borderColor !== false) {
            const borderKey = module.legacyNames?.borderColor || module.prefix
            borders[borderKey] = undefined
          }
        })

        // Generate from pages
        this.brandingDefinitions.pages.forEach((module) => {
          // borderColor defaults to true unless explicitly set to false
          if (module.borderColor !== false) {
            const borderKey = module.legacyNames?.borderColor || module.prefix
            borders[borderKey] = undefined
          }

          // Handle docs association with securityLibrary
          if (module.legacyNames?.docsBorderColor) {
            borders[module.legacyNames.docsBorderColor] = undefined
          }
        })
        return borders
      }

      // Base structure without gradient properties
      const baseStructure = {
        dataTypesWhiteIcons: undefined,
        docIconsColor: undefined,
        buttonIconsColor: undefined,
        navbarBg: undefined,
        sidebarLinkActiveColor: undefined,
        sidebarLinkColor: undefined,
        groupColor: undefined,
        groupActiveColor: undefined,
        documentTitleColor: undefined,
        documentCheckboxBorderColor: undefined,
        documentCheckboxBgColor: undefined,
        documentCheckboxCheckColor: undefined,
        documentTitleFontSize: undefined,
        documentTitleOverviewFontSize: undefined,
        controlTitleColor: undefined,
        updateTitleColor: undefined,
        updateTitleColorNew: undefined,
        controlTitleFontSize: undefined,
        complianceHeaderPageFontSize: undefined,
        complianceSeeMoreLinkColor: undefined,
        complianceCaptionFontSize: undefined,
        updateTitleFontSize: undefined,
        updateTitleFontSizeNew: undefined,
        updateTitleCaptionFontSize: undefined,
        updateTitleCaptionFontSizeNew: undefined,
        updateTitleDateFontSize: undefined,
        updateTitleDateFontSizeNew: undefined,
        controlTitleCaptionFontSize: undefined,
        controlTitleCaptionColor: undefined,
        updateTitleCaptionColor: undefined,
        updateTitleCaptionColorNew: undefined,
        controlTitleLinkColor: undefined,
        updateTitleLinkColor: undefined,
        updateTitleLinkColorNew: undefined,
        updateTitleDateColor: undefined,
        updateTitleDateColorNew: undefined,
        securityConfigColor: undefined,
        securityConfigFontSize: undefined,
        mainInputPlaceholder: undefined,
        mainInputPlaceholderColor: undefined,
        mainInputPlaceholderBorderColor: undefined,
        mainInputPlaceholderIconColor: undefined,
        seeMoreLinkColor: undefined,
        products: {
          showOverall: undefined,
          textOverall: undefined,
        },
        colors: {
          buttonColor: undefined,
          buttonColorText: undefined,
          buttonColorCustom: undefined,
          trustCenterLogoColor: undefined,
          trustCenterSeparatorColor: undefined,
          bottomNavbarBgColor: undefined,
        },
        buttonShape: {
          shape: undefined,
        },

        textsVisibility: generateVisibilityProperties(),
        overview: generateOverviewProperties(),
        fonts: {
          globalColor: undefined,
          paragraphsGlobalColor: undefined,
          globalHeadersColor: undefined,
          globalHeadersWeight: undefined,
          globalFont: undefined,
          paragraphsGlobalFont: undefined,
          globalHeadersFont: undefined,
        },

        bordersColor: generateBorderProperties(),
      }

      // Dynamically build sectionsGradientBg from gradient definitions
      const sectionsGradientBg = {}
      this.brandingDefinitions.gradients.forEach((gradient) => {
        sectionsGradientBg[gradient.prefix] = undefined
      })

      // Dynamically build sectionsBgColor with both regular and gradient properties
      const sectionsBgColor = {
        topNavbarBg: undefined,
        topNavbarBgGradient: {
          firstColor: undefined,
          secondColor: undefined,
        },
      }

      this.brandingDefinitions.gradients.forEach((gradient) => {
        // Add regular color property
        sectionsBgColor[gradient.prefix] = undefined
        // Add gradient property
        sectionsBgColor[`${gradient.prefix}Gradient`] = {
          firstColor: undefined,
          secondColor: undefined,
        }
      })

      let result = {
        ...baseStructure,
        sectionsGradientBg,
        sectionsBgColor,
      }
      console.log(
        'Inside BrandingStructure - baseStructure , sectionsGradientBg , sectionsBgColor , result:',
        baseStructure,
        sectionsGradientBg,
        sectionsBgColor,
        result
      )
      return result
    },
  },
}
